import { useEffect, useRef, useState } from 'react';

type Testimonial = {
  name: string;
  rating: number;
  text: string;
  image: string | null;
};

const testimonials: Testimonial[] = [
  {
    name: 'Hausverwaltung in Berlin-Mitte',
    rating: 5,
    text: 'Schnelle Reaktion, saubere Dokumentation und klare Kommunikation. Der Einsatz auf unserem Privatgrundstück lief professionell und ohne unnötigen Aufwand für unsere Verwaltung.',
    image: null
  },
  {
    name: 'Gewerbeobjekt an der Stadtautobahn',
    rating: 5,
    text: 'Bei einer akuten Zufahrtsblockade war das Team kurzfristig verfügbar. Vor Ort wurde strukturiert gearbeitet und der Vorgang nachvollziehbar dokumentiert.',
    image: null
  },
  {
    name: 'Eigentümergemeinschaft in Neukölln',
    rating: 5,
    text: 'Mehrfach Probleme mit unberechtigten Fahrzeugen auf dem Hof. Die Abwicklung war zuverlässig, rechtssicher und für uns als Ansprechpartner sehr unkompliziert.',
    image: null
  },
  {
    name: 'Privatparkplatz in Pankow',
    rating: 5,
    text: 'Vom ersten Kontakt bis zur Umsetzung lief alles klar und transparent. Besonders positiv war die schnelle Abstimmung und die saubere Ausführung vor Ort.',
    image: null
  },
  {
    name: 'Wohnanlage in Kreuzberg',
    rating: 5,
    text: 'Auch außerhalb der üblichen Zeiten erreichbar und einsatzbereit. Der Vorgang wurde professionell übernommen, ohne dass wir lange hinterhertelefonieren mussten.',
    image: null
  },
  {
    name: 'Betriebsgelände in Charlottenburg',
    rating: 5,
    text: 'Zuverlässiger Partner für blockierte Stellflächen und Zufahrten. Klare Prozesse, seriöses Auftreten und eine Ausführung, die man ohne Bedenken weiterempfehlen kann.',
    image: null
  }
];

function TestimonialCard({
  testimonial,
  className = ''
}: {
  testimonial: Testimonial;
  className?: string;
}) {
  return (
    <article
      className={`relative flex flex-col h-full overflow-hidden rounded-[1.9rem] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)] px-6 py-6 md:px-7 md:py-7 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_60px_rgba(37,99,235,0.12)] transition-all duration-300 ${className}`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-sky-100/70 via-transparent to-transparent" />
      <div className="flex items-center gap-4 mb-4">
        <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-sky-200 bg-gradient-to-br from-sky-400 to-blue-600 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.18)]">
          {testimonial.image ? (
            <img
              src={testimonial.image}
              alt={testimonial.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{testimonial.name.charAt(0)}</span>
          )}
        </div>

        <div className="flex-1">
          <h4 className="text-lg md:text-xl font-semibold leading-snug text-slate-900">
            {testimonial.name}
          </h4>
          <div className="mt-1 flex items-center gap-1.5">
            {Array.from({ length: testimonial.rating }).map((_, idx) => (
              <svg
                key={idx}
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-3.5 w-3.5 text-sky-500"
                fill="currentColor"
              >
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            ))}
          </div>
        </div>
      </div>

      <p className="text-sm md:text-[0.95rem] leading-relaxed italic text-slate-600">
        “{testimonial.text}”
      </p>
    </article>
  );
}

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const mobileScrollRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef<number | null>(null);
  const currentXRef = useRef<number | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth >= 768) return;

      setActiveIndex((prev) => {
        const next = (prev + 1) % testimonials.length;
        const container = mobileScrollRef.current;
        if (container) {
          const width = container.clientWidth;
          container.scrollTo({
            left: next * width,
            behavior: 'smooth'
          });
        }
        return next;
      });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;
    isDraggingRef.current = true;
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    currentXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isDraggingRef.current || startXRef.current === null || currentXRef.current === null) {
      isDraggingRef.current = false;
      startXRef.current = null;
      currentXRef.current = null;
      return;
    }

    const diff = currentXRef.current - startXRef.current;
    const THRESHOLD = 50;

    if (Math.abs(diff) > THRESHOLD) {
      if (diff < 0) {
        setActiveIndex((prev) => {
          const next = (prev + 1) % testimonials.length;
          const container = mobileScrollRef.current;
          if (container) {
            const width = container.clientWidth;
            container.scrollTo({
              left: next * width,
              behavior: 'smooth'
            });
          }
          return next;
        });
      } else {
        setActiveIndex((prev) => {
          const next = (prev - 1 + testimonials.length) % testimonials.length;
          const container = mobileScrollRef.current;
          if (container) {
            const width = container.clientWidth;
            container.scrollTo({
              left: next * width,
              behavior: 'smooth'
            });
          }
          return next;
        });
      }
    }

    isDraggingRef.current = false;
    startXRef.current = null;
    currentXRef.current = null;
  };

  const handleHorizontalScroll = () => {
    const container = mobileScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth } = container;
    if (!clientWidth) return;

    const index = Math.round(scrollLeft / clientWidth);
    setActiveIndex(index);
  };

  return (
    <section
      data-active-index={activeIndex}
      className="relative overflow-hidden py-24 md:py-28 bg-[#f8fbff]"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.08),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(96,165,250,0.06),transparent_34%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.7),rgba(255,255,255,0.95))]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-xl">
            <p className="text-[0.72rem] tracking-[0.22em] uppercase mb-4 text-sky-600">
              Kundenstimmen
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] font-semibold mb-4 leading-tight text-slate-900">
              Erfahrungen aus echten Einsätzen
            </h2>
          </div>
          <p className="text-sm md:text-base max-w-xl leading-relaxed text-slate-600">
            Von Hausverwaltungen über Eigentümergemeinschaften bis zu Gewerbestandorten: Entscheidend sind schnelle Erreichbarkeit, klare Abläufe und eine saubere Dokumentation. Genau darauf kommt es bei professionellen Einsätzen an.
          </p>
        </div>

        {/* Mobile: Scroll-/Auto-Carousel im Service-Style */}
        <div className="mt-10 md:hidden">
          <div className="relative overflow-hidden">
            <div
              ref={mobileScrollRef}
              className="flex w-full overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onScroll={handleHorizontalScroll}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="w-full flex-shrink-0 snap-center px-1">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5 flex items-center justify-center gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Gehe zu Bewertung ${index + 1}`}
                onClick={() => {
                  setActiveIndex(index);
                  const container = mobileScrollRef.current;
                  if (container) {
                    const width = container.clientWidth;
                    container.scrollTo({ left: index * width, behavior: 'smooth' });
                  }
                }}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  activeIndex === index ? 'w-7 bg-sky-500' : 'w-2.5 bg-slate-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: 3-Spalten Grid */}
        <div className="mt-12 hidden md:block">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="h-full">
                <TestimonialCard testimonial={testimonial} className="h-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
